import {useEffect} from "react";
import {useDispatch, useSelector} from "react-redux";
import {toast} from "react-toastify";
import {
  cloneProgressUpdated,
  cloneSensorsReset,
} from "../../redux/Actions/cloneProfile.actions";
import {requestSummaryData} from "../../redux/Actions/controllers.actions";
import {cloneSensorsStatusAsync} from "../../redux/AsyncActions/cloneProfile.async";
import {
  getCloneTargetMac,
  getCloneTaskId,
} from "../../redux/Selectors/cloneProfile.selectors";

const POLL_INTERVAL_MS = 3000;
const MAX_POLLS = 60;

// Mounted once at the app root (see App.jsx), independent of the
// CloneProfile modal, any route, or whether the user is even looking at
// the target controller. taskId/targetMac survive a reload via
// cloneProfile.reducer.js's localStorage mirror, so this resumes polling
// on load too — the completion toast fires whether the user closed the
// progress section, refreshed the page, or navigated elsewhere while the
// clone was still running.
const CloneSensorsWatcher = () => {
  const dispatch = useDispatch();
  const taskId = useSelector(getCloneTaskId);
  const targetMac = useSelector(getCloneTargetMac);

  useEffect(() => {
    if (!taskId || !targetMac) return;

    let cancelled = false;
    let timerId;
    let polls = 0;

    const poll = async () => {
      polls += 1;
      try {
        const res = await cloneSensorsStatusAsync(targetMac, taskId);
        if (cancelled) return;

        const data = res?.data?.data;
        const state = data?.state;

        if (state === "SUCCESS") {
          toast.success("Sensors cloned successfully.");
          dispatch(requestSummaryData(true));
          dispatch(cloneSensorsReset());
          return;
        }

        if (state === "FAILURE") {
          toast.error(data?.error || "Cloning failed on the server.");
          dispatch(cloneSensorsReset());
          return;
        }

        if (state === "PROGRESS" && data?.progress) {
          dispatch(cloneProgressUpdated(data.progress.current, data.progress.total));
        }

        if (polls >= MAX_POLLS) {
          toast.error(
            "This job is taking longer than expected and it's running in the background."
          );
          dispatch(cloneSensorsReset());
          return;
        }

        timerId = setTimeout(poll, POLL_INTERVAL_MS);
      } catch (err) {
        if (!cancelled) {
          // A response error is already toasted with the server's actual
          // message by the axiosV2 response interceptor — err.message
          // here would just be Axios's generic "Request failed with
          // status code ..." string. Only fall back for a genuine
          // network-level failure (no response at all).
          if (!err?.response) {
            toast.error(err?.message || "Cloning failed. Please try again.");
          }
          dispatch(cloneSensorsReset());
        }
      }
    };

    timerId = setTimeout(poll, POLL_INTERVAL_MS);

    return () => {
      cancelled = true;
      clearTimeout(timerId);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [taskId, targetMac]);

  return null;
};

export default CloneSensorsWatcher;
